import { useState, type ReactNode } from "react";
import { openAuditedPageLink } from "./cacheBuster";
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  Ticket,
  ExternalLink,
  CheckCircle2,
  Clock,
  LifeBuoy,
  Loader2,
  Play,
  RotateCcw,
  List as ListIcon,
  LayoutGrid,
  Table as TableIcon,
  User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  useManualFixTickets,
  useReconcileTickets,
  useSetTicketState,
  useExperts,
  useSetTicketAssignee,
} from "@/hooks/useTechnicalAuditIssues";
import { getIssueTypeLabel } from "@/types/technicalAudit";
import type { ManualFixTicket, TicketState } from "@/types/technicalAudit";

type ViewMode = "list" | "kanban" | "table";

const STATES: { key: TicketState; label: string }[] = [
  { key: "open", label: "Open" },
  { key: "in_progress", label: "In progress" },
  { key: "resolved", label: "Resolved" },
];

const STATE_STYLE: Record<TicketState, string> = {
  open: "border-coral/40 bg-coral-light text-coral-dark",
  in_progress: "border-teal/40 bg-teal-light text-teal-dark",
  resolved: "border-border bg-muted text-muted-foreground",
};

function timeAgo(iso: string | null): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function pagePath(url: string | null): string {
  if (!url) return "(unknown page)";
  try {
    const u = new URL(url);
    return u.pathname === "/" ? "/ (homepage)" : u.pathname;
  } catch {
    return url;
  }
}

type MoveFn = (issueId: string, state: TicketState) => void;

function MoveButtons({
  ticket,
  onMove,
  pending,
}: {
  ticket: ManualFixTicket;
  onMove: MoveFn;
  pending: boolean;
}) {
  const spin = pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null;
  if (ticket.state === "resolved") {
    return (
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={pending}
        onClick={() => onMove(ticket.issue_id, "open")}
        className="gap-1.5"
      >
        {spin ?? <RotateCcw className="h-3.5 w-3.5" />} Reopen
      </Button>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {ticket.state === "open" && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={pending}
          onClick={() => onMove(ticket.issue_id, "in_progress")}
          className="gap-1.5"
        >
          {spin ?? <Play className="h-3.5 w-3.5" />} Start
        </Button>
      )}
      {ticket.state === "in_progress" && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={pending}
          onClick={() => onMove(ticket.issue_id, "open")}
          className="gap-1.5"
        >
          <RotateCcw className="h-3.5 w-3.5" /> Back
        </Button>
      )}
      <Button
        type="button"
        size="sm"
        disabled={pending}
        onClick={() => onMove(ticket.issue_id, "resolved")}
        className="gap-1.5 bg-teal text-white hover:bg-teal-hover"
      >
        {spin ?? <CheckCircle2 className="h-3.5 w-3.5" />} Resolve
      </Button>
    </div>
  );
}

function StatusSelect({
  ticket,
  onMove,
  pending,
}: {
  ticket: ManualFixTicket;
  onMove: MoveFn;
  pending: boolean;
}) {
  return (
    <label className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
      Status
      <select
        value={ticket.state}
        disabled={pending}
        onChange={(e) => onMove(ticket.issue_id, e.target.value as TicketState)}
        className="rounded border border-border bg-card px-1.5 py-0.5 text-xs font-medium text-foreground focus:border-teal focus:outline-none disabled:opacity-60"
        aria-label="Change status"
      >
        {STATES.map((s) => (
          <option key={s.key} value={s.key}>
            {s.label}
          </option>
        ))}
      </select>
      {pending && <Loader2 className="h-3 w-3 animate-spin" />}
    </label>
  );
}

function AssignControl({ ticket }: { ticket: ManualFixTicket }) {
  const { data } = useExperts();
  const assign = useSetTicketAssignee();
  const experts = data?.experts ?? [];
  if (experts.length === 0) {
    // No roster configured — show the assignee name if the ticket carries one.
    return ticket.assignee_name ? (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <User className="h-3 w-3" /> {ticket.assignee_name}
      </span>
    ) : null;
  }
  return (
    <label className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      <User className="h-3 w-3" />
      <select
        value={ticket.assignee ?? ""}
        disabled={assign.isPending}
        onChange={(e) =>
          assign.mutate({ issueId: ticket.issue_id, expertId: e.target.value })
        }
        className="max-w-[130px] rounded border border-border bg-card px-1.5 py-0.5 text-xs text-foreground focus:border-teal focus:outline-none"
        aria-label="Assign expert"
      >
        <option value="">Unassigned</option>
        {experts.map((x) => (
          <option key={x.id} value={x.id}>
            {x.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function TicketMeta({ ticket }: { ticket: ManualFixTicket }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
      <span className="inline-flex items-center gap-1">
        <Clock className="h-3 w-3" /> Filed {timeAgo(ticket.requested_at)}
        {ticket.requested_by ? ` · ${ticket.requested_by}` : ""}
      </span>
      {ticket.state === "resolved" && (
        <span className="inline-flex items-center gap-1 text-teal-dark">
          <CheckCircle2 className="h-3 w-3" /> {timeAgo(ticket.resolved_at)}
          {ticket.resolved_by ? ` · ${ticket.resolved_by}` : ""}
        </span>
      )}
      {ticket.external_ticket_url && (
        <a
          href={ticket.external_ticket_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-teal-dark hover:text-teal"
        >
          <ExternalLink className="h-3 w-3" /> View ticket
        </a>
      )}
    </div>
  );
}

function TicketCard({
  ticket,
  onMove,
  onOpen,
  pending,
  dense,
}: {
  ticket: ManualFixTicket;
  onMove: MoveFn;
  onOpen: (ticket: ManualFixTicket) => void;
  pending: boolean;
  dense?: boolean;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(ticket)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(ticket);
        }
      }}
      title="View ticket details"
      className="cursor-pointer rounded-lg border border-border bg-card p-3 shadow-sm transition-colors hover:border-teal/50 hover:shadow-md focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/40 active:cursor-grabbing"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded bg-teal-light px-2 py-0.5 text-xs font-semibold text-teal-dark">
          {getIssueTypeLabel(ticket.issue_type)}
        </span>
        {ticket.severity && !dense && (
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {ticket.severity}
          </span>
        )}
      </div>
      <p
        className="mt-1.5 block truncate font-medium text-foreground"
        title={ticket.page_url ?? ""}
      >
        {pagePath(ticket.page_url)}
      </p>
      {ticket.reason && (
        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
          {ticket.reason}
        </p>
      )}
      {/* Interactive footer: don't let its clicks open the detail modal, and
          don't let a pointer-down here start a drag. */}
      <div
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        role="presentation"
      >
        <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2 border-t border-border/70 pt-2.5">
          <TicketMeta ticket={ticket} />
          <AssignControl ticket={ticket} />
        </div>
        <div className="mt-2">
          <StatusSelect ticket={ticket} onMove={onMove} pending={pending} />
        </div>
      </div>
    </div>
  );
}

function DroppableColumn({
  state,
  children,
}: {
  state: TicketState;
  children: ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: state });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "rounded-xl p-3 transition-colors",
        isOver ? "bg-teal-light/70 ring-2 ring-teal/50" : "bg-muted/70",
      )}
    >
      {children}
    </div>
  );
}

function DraggableTicket({
  ticket,
  children,
}: {
  ticket: ManualFixTicket;
  children: ReactNode;
}) {
  const { setNodeRef, listeners, transform, isDragging } = useDraggable({
    id: ticket.issue_id,
  });
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 30 }
    : undefined;
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      className={cn("touch-none", isDragging && "opacity-60")}
    >
      {children}
    </div>
  );
}

function KanbanView({
  data,
  onMove,
  onOpen,
  pendingId,
}: {
  data: { open: ManualFixTicket[]; in_progress: ManualFixTicket[]; resolved: ManualFixTicket[] };
  onMove: MoveFn;
  onOpen: (ticket: ManualFixTicket) => void;
  pendingId: string | null;
}) {
  const cols: Record<TicketState, ManualFixTicket[]> = {
    open: data.open,
    in_progress: data.in_progress,
    resolved: data.resolved,
  };
  const all = [...data.open, ...data.in_progress, ...data.resolved];
  // distance:6 so a click (opens the modal) and taps on inner controls aren't
  // treated as a drag — only a real drag moves the card.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over) return;
    const issueId = String(active.id);
    const target = String(over.id) as TicketState;
    const ticket = all.find((t) => t.issue_id === issueId);
    if (ticket && ticket.state !== target && STATES.some((s) => s.key === target)) {
      onMove(issueId, target);
    }
  };

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {STATES.map((st) => (
          <DroppableColumn key={st.key} state={st.key}>
            <div
              className={cn(
                "mb-3 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-semibold",
                STATE_STYLE[st.key],
              )}
            >
              {st.label}
              <span className="rounded-full bg-card/70 px-1.5 text-xs">
                {cols[st.key].length}
              </span>
            </div>
            <div className="min-h-[48px] space-y-2.5">
              {cols[st.key].length === 0 ? (
                <p className="px-1 py-6 text-center text-xs text-muted-foreground">
                  Drop a ticket here
                </p>
              ) : (
                cols[st.key].map((t) => (
                  <DraggableTicket key={t.issue_id} ticket={t}>
                    <TicketCard
                      ticket={t}
                      onMove={onMove}
                      onOpen={onOpen}
                      pending={pendingId === t.issue_id}
                      dense
                    />
                  </DraggableTicket>
                ))
              )}
            </div>
          </DroppableColumn>
        ))}
      </div>
    </DndContext>
  );
}

function TableView({
  tickets,
  onMove,
  onOpen,
  pendingId,
}: {
  tickets: ManualFixTicket[];
  onMove: MoveFn;
  onOpen: (ticket: ManualFixTicket) => void;
  pendingId: string | null;
}) {
  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-card">
      <table className="w-full min-w-[720px] text-sm">
        <thead>
          <tr className="border-b border-border bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="px-3 py-2 font-semibold">Page</th>
            <th className="px-3 py-2 font-semibold">Issue</th>
            <th className="px-3 py-2 font-semibold">Status</th>
            <th className="px-3 py-2 font-semibold">Filed</th>
            <th className="px-3 py-2 font-semibold">By</th>
            <th className="px-3 py-2 font-semibold">Assignee</th>
            <th className="px-3 py-2 font-semibold" />
          </tr>
        </thead>
        <tbody>
          {tickets.map((t) => (
            <tr
              key={t.issue_id}
              onClick={() => onOpen(t)}
              className="cursor-pointer border-b border-border/60 transition-colors last:border-0 hover:bg-teal-light/40"
              title="View ticket details"
            >
              <td className="max-w-[240px] truncate px-3 py-2">
                <a
                  href={t.page_url ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.stopPropagation();
                    openAuditedPageLink(e, t.page_url, t.issue_type);
                  }}
                  className="text-foreground hover:text-coral"
                  title={t.page_url ?? ""}
                >
                  {pagePath(t.page_url)}
                </a>
              </td>
              <td className="px-3 py-2 text-muted-foreground">
                {getIssueTypeLabel(t.issue_type)}
              </td>
              <td className="px-3 py-2">
                <span
                  className={cn(
                    "inline-flex rounded-full border px-2 py-0.5 text-xs font-medium",
                    STATE_STYLE[t.state],
                  )}
                >
                  {STATES.find((s) => s.key === t.state)?.label}
                </span>
              </td>
              <td className="px-3 py-2 text-muted-foreground">
                {timeAgo(t.requested_at)}
              </td>
              <td className="max-w-[160px] truncate px-3 py-2 text-muted-foreground">
                {t.requested_by ?? "—"}
              </td>
              <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                <AssignControl ticket={t} />
              </td>
              <td
                className="px-3 py-2 text-right"
                onClick={(e) => e.stopPropagation()}
              >
                <MoveButtons ticket={t} onMove={onMove} pending={pendingId === t.issue_id} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ListView({
  data,
  onMove,
  onOpen,
  pendingId,
}: {
  data: { open: ManualFixTicket[]; in_progress: ManualFixTicket[]; resolved: ManualFixTicket[] };
  onMove: MoveFn;
  onOpen: (ticket: ManualFixTicket) => void;
  pendingId: string | null;
}) {
  const groups: { key: TicketState; label: string; items: ManualFixTicket[] }[] = [
    { key: "open", label: "Open · needs a person", items: data.open },
    { key: "in_progress", label: "In progress", items: data.in_progress },
    { key: "resolved", label: "Resolved", items: data.resolved },
  ];
  return (
    <div className="space-y-6">
      {groups
        .filter((g) => g.items.length > 0)
        .map((g) => (
          <section key={g.key} className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {g.label} ({g.items.length})
            </h3>
            {g.items.map((t) => (
              <TicketCard
                key={t.issue_id}
                ticket={t}
                onMove={onMove}
                onOpen={onOpen}
                pending={pendingId === t.issue_id}
              />
            ))}
          </section>
        ))}
    </div>
  );
}

function TicketDetailModal({
  ticket,
  onClose,
  onMove,
  pending,
}: {
  ticket: ManualFixTicket | null;
  onClose: () => void;
  onMove: MoveFn;
  pending: boolean;
}) {
  return (
    <Dialog open={!!ticket} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        {ticket && (
          <>
            <DialogHeader>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded bg-teal-light px-2 py-0.5 text-xs font-semibold text-teal-dark">
                  {getIssueTypeLabel(ticket.issue_type)}
                </span>
                <span
                  className={cn(
                    "inline-flex rounded-full border px-2 py-0.5 text-xs font-medium",
                    STATE_STYLE[ticket.state],
                  )}
                >
                  {STATES.find((s) => s.key === ticket.state)?.label}
                </span>
                {ticket.severity && (
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {ticket.severity}
                  </span>
                )}
              </div>
              <DialogTitle className="mt-2 break-words text-base">
                {pagePath(ticket.page_url)}
              </DialogTitle>
              {ticket.page_url && (
                <a
                  href={ticket.page_url}
                  onClick={(e) =>
                    openAuditedPageLink(e, ticket.page_url, ticket.issue_type)
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 break-all text-xs text-teal-dark hover:text-teal"
                >
                  <ExternalLink className="h-3 w-3 shrink-0" /> {ticket.page_url}
                </a>
              )}
            </DialogHeader>

            <div className="space-y-4 text-sm">
              {ticket.reason && (
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Reason
                  </p>
                  <p className="whitespace-pre-wrap text-foreground">{ticket.reason}</p>
                </div>
              )}

              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Timeline
                </p>
                <ul className="space-y-1 text-muted-foreground">
                  <li className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 shrink-0" /> Filed {timeAgo(ticket.requested_at)}
                    {ticket.requested_by ? ` · ${ticket.requested_by}` : ""}
                  </li>
                  {ticket.started_at && (
                    <li className="flex items-center gap-1.5">
                      <Play className="h-3.5 w-3.5 shrink-0" /> Started {timeAgo(ticket.started_at)}
                    </li>
                  )}
                  {ticket.resolved_at && (
                    <li className="flex items-center gap-1.5 text-teal-dark">
                      <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> Resolved{" "}
                      {timeAgo(ticket.resolved_at)}
                      {ticket.resolved_by ? ` · ${ticket.resolved_by}` : ""}
                    </li>
                  )}
                </ul>
                {ticket.resolved_note && (
                  <p className="mt-1.5 whitespace-pre-wrap rounded bg-muted px-2 py-1 text-xs text-muted-foreground">
                    {ticket.resolved_note}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/70 pt-3">
                <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                  <User className="h-3.5 w-3.5" />
                  {ticket.assignee_name ? ticket.assignee_name : "Unassigned"}
                </span>
                <AssignControl ticket={ticket} />
              </div>

              {ticket.external_ticket_url && (
                <a
                  href={ticket.external_ticket_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-teal-dark hover:text-teal"
                >
                  <ExternalLink className="h-4 w-4" /> View external ticket
                </a>
              )}
            </div>

            <DialogFooter className="mt-2">
              <MoveButtons ticket={ticket} onMove={onMove} pending={pending} />
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function TicketsBoard({ tenantId }: { tenantId: string | null | undefined }) {
  const { data, isLoading, isError } = useManualFixTickets(tenantId);
  const setState = useSetTicketState();
  const reconcile = useReconcileTickets();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>("kanban");
  const [detailId, setDetailId] = useState<string | null>(null);

  const onMove: MoveFn = (issueId, state) => {
    setPendingId(issueId);
    setState.mutate({ issueId, state }, { onSettled: () => setPendingId(null) });
  };
  const onOpen = (ticket: ManualFixTicket) => setDetailId(ticket.issue_id);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading tickets…
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        Couldn't load tickets right now.
      </div>
    );
  }

  const counts = data.counts;
  const total = counts.open + counts.in_progress + counts.resolved;
  const grouped = {
    open: data.open,
    in_progress: data.in_progress,
    resolved: data.resolved,
  };
  const flat = [...data.open, ...data.in_progress, ...data.resolved];
  // Resolve the open ticket from the fresh list by id so the modal reflects
  // state/assignee changes made from inside it.
  const detailTicket = detailId
    ? flat.find((t) => t.issue_id === detailId) ?? null
    : null;

  const viewBtns: { key: ViewMode; label: string; icon: JSX.Element }[] = [
    { key: "kanban", label: "Kanban", icon: <LayoutGrid className="h-4 w-4" /> },
    { key: "list", label: "List", icon: <ListIcon className="h-4 w-4" /> },
    { key: "table", label: "Table", icon: <TableIcon className="h-4 w-4" /> },
  ];

  return (
    <div className="mx-auto max-w-5xl pb-8">
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-light">
          <LifeBuoy className="h-5 w-5 text-teal-dark" />
        </div>
        <div className="mr-auto">
          <h2 className="text-lg font-semibold text-foreground">Expert fix tickets</h2>
          <p className="text-sm text-muted-foreground">
            Issues handed off for a manual fix — across all crawls.
          </p>
        </div>

        <div className="inline-flex items-center gap-1 rounded-lg bg-muted p-1">
          {viewBtns.map((v) => (
            <button
              key={v.key}
              type="button"
              onClick={() => setView(v.key)}
              className={cn(
                "flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors",
                view === v.key
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {v.icon}
              <span className="hidden sm:inline">{v.label}</span>
            </button>
          ))}
        </div>

        {tenantId && counts.open > 0 && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={reconcile.isPending}
            onClick={() => reconcile.mutate(tenantId)}
            className="gap-1.5"
            title="Re-check open tickets and auto-close any whose link is already fixed"
          >
            {reconcile.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <CheckCircle2 className="h-3.5 w-3.5" />
            )}
            Re-check
          </Button>
        )}
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {STATES.map((s) => (
          <span
            key={s.key}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium",
              STATE_STYLE[s.key],
            )}
          >
            <Ticket className="h-4 w-4" /> {counts[s.key]} {s.label.toLowerCase()}
          </span>
        ))}
      </div>

      {total === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-warm-bg py-16 text-center">
          <CheckCircle2 className="mx-auto mb-2 h-7 w-7 text-teal-dark" />
          <p className="font-medium text-foreground">No tickets</p>
          <p className="text-sm text-muted-foreground">
            Nothing needs a manual fix. Use “Request expert fix” on a failed issue to
            file one.
          </p>
        </div>
      ) : view === "kanban" ? (
        <KanbanView data={grouped} onMove={onMove} onOpen={onOpen} pendingId={pendingId} />
      ) : view === "table" ? (
        <TableView tickets={flat} onMove={onMove} onOpen={onOpen} pendingId={pendingId} />
      ) : (
        <ListView data={grouped} onMove={onMove} onOpen={onOpen} pendingId={pendingId} />
      )}

      <TicketDetailModal
        ticket={detailTicket}
        onClose={() => setDetailId(null)}
        onMove={onMove}
        pending={pendingId === detailId}
      />
    </div>
  );
}
